import { useCallback, useEffect, useState } from "react";
import { errorMessage, request, RequestError } from "../lib/api";
import { getToken } from "../lib/auth";
import { track } from "../lib/events";
import type { PDDayDetail, PDDayStats, RosterResponse } from "../types";

const STATS_POLL_MS = 20000;

export interface PDDayData {
  detail: PDDayDetail;
  stats: PDDayStats;
  markedCount: number;
}

export interface PDDayState {
  data: PDDayData | null;
  loading: boolean;
  error: string;
  reload: () => Promise<void>;
}

type LoadedPDDayState = Omit<PDDayState, "reload">;
const emptyState: LoadedPDDayState = { data: null, loading: true, error: "" };
let cacheToken: string | null = null;
let dayCache = new Map<string, LoadedPDDayState>();

function currentCache(): Map<string, LoadedPDDayState> {
  const token = getToken();
  if (token !== cacheToken) {
    cacheToken = token;
    dayCache = new Map();
  }
  return dayCache;
}

// Loads exactly the session that was asked for. The facilitator chooses it
// from /pd-days; nothing is inferred or auto-selected here.
async function fetchPDDay(sessionID: string): Promise<PDDayData> {
  const [detail, stats, roster] = await Promise.all([
    request<PDDayDetail>(`/api/facilitator/pd-days/${sessionID}`),
    request<PDDayStats>(`/api/facilitator/pd-days/${sessionID}/stats`),
    request<RosterResponse>(`/api/facilitator/pd-days/${sessionID}/roster`),
  ]);
  return {
    detail,
    stats,
    markedCount: roster.roster.filter((entry) => entry.attendance !== "unmarked").length,
  };
}

async function loadPDDay(sessionID: string): Promise<LoadedPDDayState> {
  const token = getToken();
  try {
    const data = await fetchPDDay(sessionID);
    const next = { data, loading: false, error: "" };
    if (getToken() === token) currentCache().set(sessionID, next);
    return next;
  } catch (cause) {
    const saved = currentCache().get(sessionID);
    const next =
      saved?.data && !(cause instanceof RequestError && cause.status === 401)
        ? saved
        : { data: null, loading: false, error: errorMessage(cause) };
    if (getToken() === token) currentCache().set(sessionID, next);
    return next;
  }
}

export async function preparePDDay(sessionID: string): Promise<void> {
  if (currentCache().get(sessionID)?.data) return;
  await loadPDDay(sessionID);
}

export function usePDDay(sessionID: string): PDDayState {
  const [state, setState] = useState<LoadedPDDayState>(
    () => currentCache().get(sessionID) ?? emptyState
  );

  const reload = useCallback(async () => {
    const token = getToken();
    const data = await fetchPDDay(sessionID);
    const next = { data, loading: false, error: "" };
    if (getToken() === token) currentCache().set(sessionID, next);
    setState(next);
  }, [sessionID]);

  useEffect(() => {
    let active = true;
    track("page_opened", { payload: { page: "pd_day", session_id: sessionID } });
    void loadPDDay(sessionID).then((next) => {
      if (active) setState(next);
    });
    return () => {
      active = false;
    };
  }, [sessionID]);

  useEffect(() => {
    if (state.data?.detail.status !== "in_progress") return undefined;
    const timer = setInterval(() => void reload().catch(() => undefined), STATS_POLL_MS);
    return () => clearInterval(timer);
  }, [state.data?.detail.status, reload]);

  return { ...state, reload };
}
